<template>
  <MainLayout>
    <template
      v-slot:header
      v-if="currentState !== constants.VIEW_STATES.LOADING"
    >
      <Header :showBackButton="showBackButton" @handleBack="handleBack" />
    </template>
    <template v-slot:content>
      <div v-if="showErrorAlert">
        <FilterAlert
          :text="errorAlertText"
          :visible="showErrorAlert"
          @toggle="showErrorAlert = false"
        />
      </div>
      <Loading
        style="height: 80vh"
        v-if="currentState === constants.VIEW_STATES.LOADING"
        :progress="progress"
        @cancel="handleCancelImport"
      />

      <Filters
        v-if="currentState === constants.VIEW_STATES.FILTERS"
        hasColumnSelector
        showInstructions
        class="mb-4"
        :selectedFilters="selectedFilters"
        :typeOptions="typeOptions"
        @toggleColumnSelector="openColumnSelector"
        @change="handleFiltersChange"
      />

      <Finish
        style="height: 80vh"
        v-if="currentState === constants.VIEW_STATES.IMPORTED"
        :totalRows="totalRows"
        @startOver="onNewImport"
      />
      <Error
        style="height: 80vh"
        v-if="currentState === constants.VIEW_STATES.ERROR"
        :action="backToFiltersActionText"
        @click="handleErrorClick"
      />
      <EmptyState
        style="height: 80vh"
        v-if="currentState === constants.VIEW_STATES.EMPTY"
        :action="backToFiltersActionText"
        @click="handleEmptyStateClick"
      />
      <ColumnSelector
        :show="showColumnSelector"
        :entityType="currentEntityType"
        :selectedColumns="selectedColumnsByType[currentEntityType]"
        @save="handleSavedColumns"
        @change="handleColumnChange"
        @close="showColumnSelector = false"
      />
    </template>
    <template v-slot:footer>
      <div
        v-if="currentState === constants.VIEW_STATES.FILTERS"
        style="width: 100%"
      >
        <v-divider />
        <div class="px-6 py-4">
          <Button
            block
            icon="mdi-import"
            :text="$t('common.import')"
            @click="handleImport"
          />
        </div>
      </div>
    </template>
  </MainLayout>
</template>

<script>
import AlphanumericEncoder from 'alphanumeric-encoder'
import _ from 'lodash'

import api from '@/api'
import store from '@/store/index.js'
import user from '@/mixins/user.vue'
import constants from './constants'
import serializeExploreFilters from './serializeExploreFilters'

import MainLayout from '@/ui/shared/components/Layouts/MainLayout.vue'
import Header from '@/ui/shared/components/Header.vue'
import Button from '@/ui/shared/components/Button.vue'
import Filters from '@/ui/shared/components/Filters/Filters.vue'
import ColumnSelector from '@/ui/excel/components/ColumnSelector/ColumnSelector.vue'
import FilterAlert from '@/ui/shared/components/FilterAlert.vue'

import Finish from './States/Finish.vue'
import Loading from './States/Loading.vue'
import Error from './States/Error.vue'
import EmptyState from './States/EmptyState.vue'
import * as moment from 'moment'

import FieldMappers from './FieldMappers/index.js'
import {
  buildExcelDateNumberFormats,
  buildExcelTableWritePlan,
  buildExcelTableName,
  getExcelDateValue,
} from './excelDateFormatting'

const encoder = new AlphanumericEncoder()

const WORKSHEET_ROW_COLORS = {
  evenIndexed: '#F2F5FA',
  oddIndexed: '#E2E8F0',
  header: '#2D3748',
}

export default {
  name: 'HomeView',

  mixins: [user],

  components: {
    Header,
    MainLayout,
    ColumnSelector,
    Button,
    Filters,
    Loading,
    Finish,
    Error,
    FilterAlert,
    EmptyState,
  },

  data() {
    return {
      error: null,
      cancelImport: false,
      showErrorAlert: false,
      currentState: constants.VIEW_STATES.FILTERS,
      selectedFilters: _.cloneDeep({ ...constants.DEFAULT_EXPLORE_FILTERS }),
      selectedColumnsByType: _.cloneDeep({
        ...constants.DEFAULT_COLUMNS_BY_TYPE,
      }),
      showColumnSelector: false,
      currentEntityType: null,
      rowCursorByEntity: {
        improvementIdea: 0,
        problemSolving: 0,
        quickAction: 0,
        routine: 0,
        finding: 0,
      },
      worksheetNameByEntity: {
        improvementIdea: null,
        problemSolving: null,
        quickAction: null,
        routine: null,
        finding: null,
      },
      tableNameByEntity: {
        improvementIdea: null,
        problemSolving: null,
        quickAction: null,
        routine: null,
        finding: null,
      },
      totalRows: 0,
      constants,
      progress: 0,
    }
  },

  mounted() {
    this.selectedFilters.sites = [
      this.currentUser?.site?._id ?? this.currentUser?.site,
    ]
  },

  computed: {
    currentUser() {
      return store.access.getAccount()
    },

    backToFiltersActionText() {
      return this.$t('excel.back_to_filters')
    },

    showBackButton() {
      return this.currentState === constants.VIEW_STATES.IMPORTED || this.error
    },

    errorAlertText() {
      return this.error ?? this.$t('common.alert_text')
    },
    organizationId() {
      return store.access.getOrganizationId()
    },

    organizationStructure() {
      return store.organization.getOrganizationStructure()
    },
    organization() {
      return store.organization.getOrganization()
    },

    typeOptions() {
      return _.compact([
        this.organization?.featuresEnabled__improvementIdea && {
          id: 'improvementIdea',
          text:
            this.organization?.customNamings?.improvementIdea?.singular ||
            this.$t('common.rev_type.improvement_idea'),
        },
        this.organization?.featuresEnabled__quickAction && {
          id: 'quickAction',
          text:
            this.organization?.customNamings?.quickAction?.singular ||
            this.$t('common.rev_type.quick_action'),
        },
        this.organization?.featuresEnabled__problemSolving && {
          id: 'problemSolving',
          text:
            this.organization?.customNamings?.problemSolving?.singular ||
            this.$t('common.rev_type.problem_solving'),
        },
        this.organization?.featuresEnabled__routines && {
          id: 'routine',
          text: this.$t('common.routine'),
        },
        this.organization?.featuresEnabled__routines && {
          id: 'finding',
          text: this.$t('common.finding'),
        },
      ])
    },
  },

  methods: {
    handleErrorClick() {
      return this.onNewImport()
    },

    handleEmptyStateClick() {
      return this.onNewImport()
    },

    openColumnSelector(entity) {
      this.currentEntityType = entity
      this.showColumnSelector = true
    },

    onNewImport() {
      this.$track('msOffice.newImport', {
        env: window.Office.context.host,
      })
      this.error = null
      this.showErrorAlert = false
      this.cancelImport = false
      this.selectedColumnsByType = { ...constants.DEFAULT_COLUMNS_BY_TYPE }
      this.rowCursorByEntity = {
        improvementIdea: 0,
        problemSolving: 0,
        quickAction: 0,
        routine: 0,
        finding: 0,
      }

      this.worksheetNameByEntity = {
        improvementIdea: null,
        problemSolving: null,
        quickAction: null,
        routine: null,
        finding: null,
      }

      this.tableNameByEntity = {
        improvementIdea: null,
        problemSolving: null,
        quickAction: null,
        routine: null,
        finding: null,
      }

      this.totalRows = 0
      this.currentState = constants.VIEW_STATES.FILTERS
      this.selectedFilters = {
        ...constants.DEFAULT_EXPLORE_FILTERS,
        sites: [this.currentUser?.site?._id ?? this.currentUser?.site],
      }
      this.progress = 0
    },

    async fetchExplore({ limit, from }) {
      try {
        const request = {
          limit,
          from,
          projections: constants.EXPLORE_PROJECTIONS,
          expansions: constants.EXPLORE_EXPANSIONS,
          filters: serializeExploreFilters({
            filters: this.selectedFilters,
            currentAccountId: this.currentUser?.id,
          }),
          sort: {
            by: 'createdAt',
            order: 'desc',
          },
        }

        const response = await api.stream.getExplore(
          this.organizationId,
          request
        )

        return response.data
      } catch (err) {
        console.error(err)
        this.currentState = constants.VIEW_STATES.ERROR
        this.error = err.message
        this.showErrorAlert = true
      }
    },

    handleFiltersChange(values, filter) {
      this.selectedFilters[filter] = values
    },

    buildHeadersTitles(ids) {
      return ids.map(id => this.$t(`column_selector.${_.snakeCase(id)}`))
    },

    buildSheetNameByEntity(entityType) {
      const todayFormat = String(moment().format('YYMMDD-HHmmss')).replace(
        /\s/g,
        ''
      )
      const entityName = this.typeOptions?.find(
        option => option.id === entityType
      )
      const camelCaseName = _.camelCase(entityName.text)
      const charactersAvailable = 30 - todayFormat.length
      const shortName =
        camelCaseName.length >= charactersAvailable
          ? camelCaseName.slice(0, charactersAvailable)
          : camelCaseName

      const sanitizedName = shortName.replace(/[^\w\s]/gi, '')
      return `${sanitizedName}${todayFormat}`
    },

    getWorksheetRowRange(worksheet, rowIndex, lastColumnLetter) {
      return worksheet.getRange(`A${rowIndex}:${lastColumnLetter}${rowIndex}`)
    },

    applyWorksheetRowBorders(rowRange, rowColor) {
      rowRange.format.borders.getItem('InsideHorizontal').style = 'None'
      rowRange.format.borders.getItem('InsideVertical').style = 'None'
      rowRange.format.borders.getItem('EdgeBottom').color = rowColor
      rowRange.format.borders.getItem('EdgeLeft').style = 'None'
      rowRange.format.borders.getItem('EdgeRight').style = 'None'
      rowRange.format.borders.getItem('EdgeTop').color = rowColor
    },

    applyZebraRowStyle(worksheet, rowIndex, lastColumnLetter) {
      const rowColor =
        rowIndex % 2 === 0
          ? WORKSHEET_ROW_COLORS.evenIndexed
          : WORKSHEET_ROW_COLORS.oddIndexed

      const rowRange = this.getWorksheetRowRange(
        worksheet,
        rowIndex,
        lastColumnLetter
      )

      this.applyWorksheetRowBorders(rowRange, rowColor)
      rowRange.format.fill.color = rowColor
    },

    applyWorksheetHeaderStyle(firstRowRange) {
      firstRowRange.format.fill.color = WORKSHEET_ROW_COLORS.header
      firstRowRange.format.borders.getItem('EdgeTop').style = 'None'
    },

    createWorksheetTable({
      worksheet,
      range,
      firstRowRange,
      headers,
      rows,
      columns,
      lastColumnLetter,
      rangeString,
      newRowCursor,
      tableName,
    }) {
      range.values = [headers, ...rows]
      range.numberFormat = buildExcelDateNumberFormats(
        columns,
        rows.length,
        true
      )

      worksheet.getUsedRange().format.autofitColumns()
      worksheet.getUsedRange().format.autofitRows()

      for (let i = 1; i <= newRowCursor; i++) {
        this.applyZebraRowStyle(worksheet, i, lastColumnLetter)
      }

      this.applyWorksheetHeaderStyle(firstRowRange)

      const dataTable = worksheet.tables.add(rangeString, true)
      dataTable.name = tableName
    },

    appendRowsToWorksheetTable({
      worksheet,
      range,
      rows,
      columns,
      tableName,
      startRow,
      lastColumnLetter,
    }) {
      const dataTable = worksheet.tables.getItem(tableName)

      dataTable.rows.add(null, rows)
      range.numberFormat = buildExcelDateNumberFormats(columns, rows.length)
      range.format.autofitColumns()
      range.format.autofitRows()

      for (let i = 0; i < rows.length; i++) {
        this.applyZebraRowStyle(worksheet, startRow + i, lastColumnLetter)
      }
    },

    resolveEntityWorksheet(context, entityType) {
      const isNewEntityWorksheet = !this.worksheetNameByEntity[entityType]

      if (isNewEntityWorksheet) {
        this.worksheetNameByEntity[entityType] = this.buildSheetNameByEntity(
          entityType
        )
        this.tableNameByEntity[entityType] = buildExcelTableName(
          this.worksheetNameByEntity[entityType]
        )

        return {
          isNewEntityWorksheet,
          worksheet: context.workbook.worksheets.add(
            this.worksheetNameByEntity[entityType]
          ),
        }
      }

      return {
        isNewEntityWorksheet,
        worksheet: context.workbook.worksheets.getItem(
          this.worksheetNameByEntity[entityType]
        ),
      }
    },

    buildWorksheetWriteContext({
      worksheet,
      entityType,
      rows,
      isNewEntityWorksheet,
    }) {
      const lastColumnLetter = encoder.encode(rows[0].length)

      const { mode, startRow, endRow, newRowCursor } = buildExcelTableWritePlan(
        {
          rowCursor: this.rowCursorByEntity[entityType],
          rowCount: rows.length,
          isNewEntityWorksheet,
        }
      )

      const rangeString = `A${startRow}:${lastColumnLetter}${endRow}`
      const firstRow = `A1:${lastColumnLetter}1`

      return {
        mode,
        newRowCursor,
        lastColumnLetter,
        rangeString,
        range: worksheet.getRange(rangeString),
        firstRowRange: worksheet.getRange(firstRow),
      }
    },

    async handleImport() {
      if (_.isEmpty(this.selectedFilters.types)) {
        return (this.showErrorAlert = true)
      }
      try {
        this.$track('msOffice.import', {
          env: window.Office.context.host,
          selectedFilters: this.selectedFilters,
        })

        this.showErrorAlert = false
        this.currentState = constants.VIEW_STATES.LOADING

        const { totalRecordsInData } = await this.fetchExplore({ from: 0 })

        if (totalRecordsInData === 0) {
          return (this.currentState = constants.VIEW_STATES.EMPTY)
        }
        const limit = 25
        const totalPages = Math.ceil(totalRecordsInData / limit)

        for (let page = 1; page <= totalPages; page++) {
          if (this.cancelImport) return this.onNewImport()

          const { results } = await this.fetchExplore({
            from: (page - 1) * limit,
            limit,
          })

          const resultsByType = _.groupBy(
            results.map(item => item.response),
            'entityType'
          )

          const entityKeys = Object.keys(resultsByType)

          for (const entityType of entityKeys) {
            const headersIds = this.buildColumnsByEntityType(
              this.selectedColumnsByType[entityType],
              entityType
            )

            const rows = this.buildRowsByEntityType(
              resultsByType[entityType],
              entityType,
              headersIds
            )

            const headerTitles = this.buildHeadersTitles(headersIds)

            // try {
            await this.sendEntityItemsToExcel({
              entityType,
              columns: headersIds,
              rows,
              headers: headerTitles,
              activateSheet: page === 1 || page === totalPages,
            })
            this.totalRows = this.totalRows + rows.length

            this.progress = ((page - 1) / totalPages) * 100

            // }
            //  catch (err) {
            //   // TODO revisar porqué siempre entra en el catch
            //   console.log('err', err)
            //   console.log('Error sending items', entityType)
            // }
          }
        }

        this.currentState = constants.VIEW_STATES.IMPORTED
      } catch (err) {
        this.currentState = constants.VIEW_STATES.ERROR
        console.log('err', err)
      }
    },

    async sendEntityItemsToExcel({
      entityType,
      columns,
      headers,
      rows,
      activateSheet,
    }) {
      try {
        await window.Excel.run(async context => {
          const {
            worksheet,
            isNewEntityWorksheet,
          } = this.resolveEntityWorksheet(context, entityType)

          if (activateSheet || this.cancelImport) {
            worksheet.activate()
          }

          await context.sync()

          const {
            mode,
            newRowCursor,
            lastColumnLetter,
            rangeString,
            range,
            firstRowRange,
          } = this.buildWorksheetWriteContext({
            worksheet,
            entityType,
            rows,
            isNewEntityWorksheet,
          })

          if (mode === 'create_table') {
            this.createWorksheetTable({
              worksheet,
              range,
              firstRowRange,
              headers,
              rows,
              columns,
              lastColumnLetter,
              rangeString,
              newRowCursor,
              tableName: this.tableNameByEntity[entityType],
            })
          } else {
            this.appendRowsToWorksheetTable({
              worksheet,
              range,
              rows,
              columns,
              tableName: this.tableNameByEntity[entityType],
              startRow: newRowCursor - rows.length + 1,
              lastColumnLetter,
            })
          }

          this.rowCursorByEntity[entityType] = newRowCursor
        })
      } catch (error) {
        console.log('error', error)
        throw error
      }
    },

    handleColumnChange(column) {
      const columnIndex = this.selectedColumnsByType[
        this.currentEntityType
      ].findIndex(item => item === column)

      if (columnIndex === -1) {
        this.selectedColumnsByType[this.currentEntityType].push(column)

        return
      }

      this.selectedColumnsByType[this.currentEntityType].splice(columnIndex, 1)

      return
    },

    handleSavedColumns() {
      this.showColumnSelector = false
    },

    isRevEntity(entity) {
      return (
        entity === 'improvementIdea' ||
        entity === 'problemSolving' ||
        entity === 'quickAction'
      )
    },

    buildRowsByEntityType(results, entityType, columns) {
      return results?.map?.(result =>
        columns?.map?.(col => {
          const excelDateValue = getExcelDateValue(result, col)

          if (excelDateValue !== null) {
            return excelDateValue
          }

          return (
            FieldMappers?.[entityType]?.[col]?.(
              result,
              this.organizationStructure
            ) ?? ''
          )
        })
      )
    },

    buildColumnsByEntityType(selectedColumns, entityType) {
      let excelColumns = [...selectedColumns]

      excelColumns = this.buildLocationColumns(excelColumns)

      if (this.isRevEntity(entityType)) {
        excelColumns = this.buildRevValidationColumns(excelColumns)

        const phasesIndex = excelColumns.findIndex(col => col === 'phases')
        if (phasesIndex > 0) {
          excelColumns.splice(phasesIndex, 1)

          if (entityType === 'quickAction') {
            excelColumns = this.buildQuickActionColumns(excelColumns)
          }
          if (entityType === 'improvementIdea') {
            excelColumns = this.buildImprovementIdeaColumns(excelColumns)
          }
          if (entityType === 'problemSolving') {
            excelColumns = this.buildProblemSolvingColumns(excelColumns)
          }

          const reviewIndex = excelColumns.findIndex(col => col === 'review')
          excelColumns.splice(
            reviewIndex,
            1,
            'impactStartDate',
            'impactEndDate',
            'financialValidationDate',
            'totalImpactAmount'
          )
        }
      }

      return excelColumns
    },

    buildRevValidationColumns(excelColumns) {
      const columns = [...excelColumns]
      const validationsIndex = excelColumns.findIndex(
        col => col === 'validations'
      )
      if (validationsIndex > 0) {
        columns.splice(validationsIndex, 1, 'implemented', 'impactValidated')
      }

      return columns
    },

    buildLocationColumns(excelColumns) {
      const columns = [...excelColumns]
      const locationIndex = columns.findIndex(col => col === 'location')
      if (locationIndex > 0) {
        columns.splice(locationIndex, 1, 'country', 'site', 'siteGroups')
      }
      return columns
    },

    buildQuickActionColumns(excelColumns) {
      const columns = [...excelColumns]
      const beforeIndex = columns.findIndex(col => col === 'before')
      if (beforeIndex > 0) {
        columns.splice(beforeIndex, 1, 'problem')
      }

      const afterIndex = columns.findIndex(col => col === 'after')
      if (afterIndex > 0) {
        columns.splice(afterIndex, 1, 'action', 'problemSolver')
      }
      return columns
    },

    buildImprovementIdeaColumns(excelColumns) {
      const columns = [...excelColumns]
      const planIndex = columns.findIndex(col => col === 'plan')
      if (planIndex > 0) {
        columns.splice(planIndex, 1, 'problem')
      }

      const adjustIndex = columns.findIndex(col => col === 'adjust')
      if (adjustIndex > 0) {
        columns.splice(adjustIndex, 1, 'solution')
      }
      return columns
    },

    buildProblemSolvingColumns(excelColumns) {
      const columns = [...excelColumns]

      const defineIndex = columns.findIndex(col => col === 'define')
      if (defineIndex > 0) {
        columns.splice(defineIndex, 1, 'problem', 'plan')
      }

      const improveControlIndex = columns.findIndex(
        col => col === 'improveControl'
      )
      if (improveControlIndex > 0) {
        columns.splice(improveControlIndex, 1, 'solution')
      }
      return columns
    },

    handleBack() {
      this.$track('msOffice.backButton', {
        env: window.Office.context.host,
        screen: this.currentState,
      })
      this.currentState = constants.VIEW_STATES.FILTERS
      this.selectedFilters = {
        ...constants.DEFAULT_EXPLORE_FILTERS,
        sites: [this.currentUser?.site?._id ?? this.currentUser?.site],
      }
    },

    handleCancelImport() {
      this.cancelImport = true

      this.$track('msOffice.cancelImport', {
        env: window.Office.context.host,
      })
    },
  },
}
</script>
